import {useMessage} from '@coolcolduk/ui-mui';
import {Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Refresh as RefreshIcon} from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {useState} from 'react';
import {Link} from 'react-router';
import {z} from 'zod';
import {ImageModel, QueueJobContent, UserAccount} from '../../../__generated__/resolvers-types';
import {JobListButton} from '../../../components/jobList/JobListButton';
import useCreateImageToImageQueueJob from '../../../hook/useCreateImageToImageQueueJob';
import useDeleteGenerationJobProject from '../../../hook/generationJob/useDeleteGenerationJobProject';
import useListGenerationJobProjects from '../../../hook/generationJob/useListGenerationJobProjects';
import type {GenerationJobProject} from '../../../hook/generationJob/schema';
import useLocalStorageStateWithValidation from '../../../hook/useLocalStorageStateWithValidation';

const IMAGE_TO_IMAGE_MODEL_STORAGE_KEY = 'imageToImageModel';

const imageModelSchema = z.nativeEnum(ImageModel);

export interface ImageToImagePageProps {
  user?: UserAccount | null;
}

export default function ImageToImagePage(_props: ImageToImagePageProps) {
  const {showMessage} = useMessage();
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [projectToDelete, setProjectToDelete] = useState<GenerationJobProject | null>(null);

  const [model, setModel] = useLocalStorageStateWithValidation(
    IMAGE_TO_IMAGE_MODEL_STORAGE_KEY,
    ImageModel.XAI_IMAGINE_IMAGE,
    imageModelSchema,
  );

  const {projects, loading: listLoading, refetch: refetchProjects} = useListGenerationJobProjects();
  const {createImageToImageJob, loading: createJobLoading} = useCreateImageToImageQueueJob({
    onCompleted: () => showMessage('Generation job created'),
    onError: (e) => showMessage(`Error: ${e.message}`, undefined, 'error'),
  });
  const [deleteProject, {loading: deleteLoading}] = useDeleteGenerationJobProject({
    onCompleted: () => {
      showMessage('Project removed');
      if (projectToDelete && selectedProjectId === projectToDelete.id) setSelectedProjectId('');
      setProjectToDelete(null);
    },
    onError: (e) => showMessage(`Error: ${e.message}`, undefined, 'error'),
  });

  const handleDeleteClick = (e: React.MouseEvent, p: GenerationJobProject) => {
    e.stopPropagation();
    setProjectToDelete(p);
  };

  const handleConfirmDelete = () => {
    if (projectToDelete) {
      deleteProject(projectToDelete.id);
    } else {
      setProjectToDelete(null);
    }
  };

  const handleGenerate = () => {
    if (!selectedProjectId) {
      showMessage('Select a project first', undefined, 'warning');
      return;
    }
    createImageToImageJob({projectId: selectedProjectId, model});
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h5">Image to Image Generation</Typography>
      <Typography variant="body2" color="text.secondary">
        Use a source image and prompts from a Google Sheet to generate images via OpenAI. Sheet columns: filename,
        prompt, status (use &quot;new&quot; for rows to process).
      </Typography>

      <JobListButton content={QueueJobContent.imageToImageGeneration} />

      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{m: 2}}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Typography variant="h6">Projects</Typography>
            <Tooltip title="Refresh list">
              <IconButton
                onClick={() => refetchProjects()}
                disabled={listLoading}
                size="small"
                aria-label="Refresh list"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Stack>
          <Button component={Link} to="/image-to-image/new" variant="outlined" startIcon={<AddIcon />}>
            Add project
          </Button>
        </Stack>

        {listLoading ? (
          <Typography color="text.secondary">Loading projects…</Typography>
        ) : !projects?.length ? (
          <Typography color="text.secondary">
            No projects yet.{' '}
            <Button component={Link} to="/image-to-image/new" size="small">
              Add one
            </Button>
          </Typography>
        ) : (
          <List disablePadding sx={{border: 1, borderColor: 'divider', borderRadius: 1}}>
            {projects.map((p) => (
              <ListItemButton
                key={p.id}
                selected={selectedProjectId === p.id}
                onClick={() => setSelectedProjectId(p.id)}
              >
                <ListItemText primary={p.name || p.id} secondary={p.sheetId} />
                <ListItemSecondaryAction>
                  <Tooltip title="Edit project">
                    <IconButton
                      edge="end"
                      size="small"
                      aria-label="Edit project"
                      component={Link}
                      to={`/image-to-image/edit/${p.id}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Remove project">
                    <IconButton
                      edge="end"
                      size="small"
                      aria-label="Remove project"
                      onClick={(e) => handleDeleteClick(e, p)}
                      disabled={deleteLoading}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItemButton>
            ))}
          </List>
        )}

        <Dialog open={Boolean(projectToDelete)} onClose={() => setProjectToDelete(null)}>
          <DialogTitle>Remove project?</DialogTitle>
          <DialogContent>
            {projectToDelete ? (
              <Typography>
                This will remove the project &quot;{projectToDelete.name || projectToDelete.id}&quot;. Existing jobs are
                not affected.
              </Typography>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setProjectToDelete(null)} disabled={deleteLoading}>
              Cancel
            </Button>
            <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleteLoading}>
              Remove
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      <FormControl fullWidth>
        <InputLabel>Image model</InputLabel>
        <Select value={model} onChange={(e) => setModel(e.target.value as ImageModel)} label="Image model">
          {(Object.values(ImageModel) as ImageModel[]).map((m) => (
            <MenuItem key={m} value={m}>
              {m}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Button
        variant="contained"
        color="primary"
        onClick={handleGenerate}
        disabled={!selectedProjectId || createJobLoading}
      >
        Generate
      </Button>
    </Stack>
  );
}
